import { MaterialTopTabBar } from "@-/com.npmjs.expo-router/material-top-tabs";
import { useIsFocused } from "@-/com.npmjs.expo-router";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import { useEffect, useState } from "react";
import { Animated, View, useWindowDimensions } from "@-/com.npmjs.react-native";
import { LoopingPager, loopingPagerStep } from "@-/dev.improve.haniverse.modules.looping-pager";
import { useResolveClassNames } from "@-/com.npmjs.uniwind";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { createMaterialTopTabBarProps } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { RouteBodyStateScope } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useMnemonicTabGlyphs } from "#use-mnemonic-tab-glyphs";
import { useBrowseMnemonicTabLabels } from "#use-browse-mnemonic-tab-labels";
import type { BrowseMnemonicTabsProps } from "#browse-mnemonic-tabs-props";

/** Web reading strip; native platforms own their tab bar's presentation. Pages wrap around. */
export function BrowseMnemonicTabs({
  activeIndex,
  grapheme,
  onIndexChange,
  pagerTopExtension = 0,
  readings,
  renderTabBar,
  screens,
  swipeEnabled = true,
}: BrowseMnemonicTabsProps) {
  const { width } = useWindowDimensions();
  const isFocused = useIsFocused();
  useEffect(() => {
    if (!isFocused) {
      return;
    }
    return tinykeys(
      globalThis.window,
      {
        // Like a swipe, the arrow keys wrap between the first and last pages.
        ArrowLeft: (event) => {
          if (screens.length > 1) {
            onIndexChange(loopingPagerStep(activeIndex, -1, screens.length));
            event.preventDefault();
          }
        },
        ArrowRight: (event) => {
          if (screens.length > 1) {
            onIndexChange(loopingPagerStep(activeIndex, 1, screens.length));
            event.preventDefault();
          }
        },
      },
      {
        ignore: (event) =>
          event.target instanceof HTMLElement &&
          (event.target.isContentEditable ||
            event.target.tagName === "TEXTAREA" ||
            (event.target.tagName === "INPUT" &&
              !event.target.classList.contains("persistent-keyboard-input"))),
      },
    );
  }, [activeIndex, isFocused, onIndexChange, screens.length]);
  const backgroundStyle = useResolveClassNames("bg-background");
  const { accessibilityLabels, labels } = useBrowseMnemonicTabLabels(screens, readings);
  const { glyphs, icons, script } = useMnemonicTabGlyphs(grapheme, readings, true);
  // The strip has no pager driving it, so its position simply follows the selection.
  const [tabBarPosition] = useState(() => new Animated.Value(activeIndex));
  useEffect(() => {
    tabBarPosition.setValue(activeIndex);
  }, [activeIndex, tabBarPosition]);
  const tabBarProps = createMaterialTopTabBarProps({
    activeIndex,
    getOptions: (route) => {
      const index = screens.findIndex((screen) => screen.key === route.key);
      return {
        tabBarAccessibilityLabel: accessibilityLabels[index],
        tabBarActiveTintColor: glyphs[index]?.color,
        tabBarIcon: () =>
          route.name === "info" ? (
            <Icon className="text-foreground" name="LuBookOpen" size={18} />
          ) : readings[index] === undefined ? (
            <GraphemeFontGlyph className="text-[18px]" glyph={glyphs[index]} hanziScript={script} />
          ) : (
            <Text className="text-[16px] leading-5" style={{ color: glyphs[index]?.color }}>
              {icons[index]?.text}
            </Text>
          ),
        tabBarInactiveTintColor: glyphs[index]?.color,
        tabBarIndicatorStyle: { backgroundColor: "transparent" },
        tabBarItemStyle: {
          flexDirection: "row",
          minHeight: 40,
          paddingHorizontal: screens.length > 5 ? 10 : 0,
          paddingVertical: 4,
          ...(screens.length > 5 ? { width: "auto" } : {}),
        },
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: "400",
          margin: 0,
          textTransform: "none",
        },
        tabBarScrollEnabled: screens.length > 5,
        tabBarStyle: backgroundStyle,
        title: route.name === "info" ? "Details" : labels[index],
      };
    },
    navigate: (name) => {
      const index = screens.findIndex((screen) => screen.name === name);
      if (index !== -1) {
        onIndexChange(index);
      }
    },
    routes: screens.map((screen) => ({
      key: screen.key,
      name: screen.name,
      params: undefined,
    })),
    stateKey: "browse-readings-" + grapheme,
    withDispatchNavigation: true,
  });
  const tabBar = (
    <MaterialTopTabBar
      {...tabBarProps}
      jumpTo={(key) => {
        const index = screens.findIndex((screen) => screen.key === key);
        if (index !== -1) {
          onIndexChange(index);
        }
      }}
      layout={{ height: 0, width }}
      position={tabBarPosition}
    />
  );
  return (
    <View className="flex-1 bg-background">
      <View className="flex-1" style={{ marginTop: -pagerTopExtension }}>
        {renderTabBar?.(tabBar) ?? tabBar}
        <LoopingPager
          className="flex-1"
          index={activeIndex}
          onIndexChange={onIndexChange}
          pages={screens.map((screen) => ({
            key: screen.key,
            render: () => (
              <RouteBodyStateScope name={"reading:" + screen.key}>
                {screen.render()}
              </RouteBodyStateScope>
            ),
          }))}
          swipeEnabled={swipeEnabled}
        />
      </View>
    </View>
  );
}
